// DERWENTAI Reports — admin settings + preview + Slack test + report history.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, Send, Eye, Save, History, Loader2, Clock } from 'lucide-react';
import { ReportStatusCard } from '../components/ReportStatusCard';

const API = process.env.REACT_APP_BACKEND_URL;
const REPORTS = [
  { key: 'morning', label: 'Morning Update' },
  { key: 'midday', label: 'Midday Update' },
  { key: 'eod', label: 'End of Day Update' },
];

export default function DerwentAIReports() {
  const token = localStorage.getItem('auth_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewing, setPreviewing] = useState('');
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, h, st] = await Promise.all([
        axios.get(`${API}/api/derwentai/reports/config`, { headers }),
        axios.get(`${API}/api/derwentai/reports/history?limit=30`, { headers }),
        axios.get(`${API}/api/derwentai/reports/status`, { headers }),
      ]);
      setCfg(c.data); setHistory(h.data || []); setStatus(st.data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error('Could not load settings');
    }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/derwentai/reports/config`, {
        channel: cfg.channel, model: cfg.model, reports: cfg.reports,
        targets: cfg.targets, alerts: cfg.alerts,
      }, { headers });
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  const doPreview = async (type) => {
    setPreviewing(type); setPreviewText('');
    try {
      const { data } = await axios.get(`${API}/api/derwentai/reports/preview?type=${type}`, { headers });
      setPreviewText(data.message || '');
    } catch { toast.error('Preview failed'); } finally { setPreviewing(''); }
  };

  const sendTest = async (type) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/derwentai/reports/test`, { type }, { headers });
      if (data.status === 'sent') toast.success(`Sent ${type} report to Slack`);
      else toast.error(`Not sent: ${(data.errors || []).join(', ') || 'check channel'}`);
      load();
    } catch { toast.error('Send failed'); } finally { setBusy(false); }
  };

  const sendTestAlert = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/derwentai/reports/test-alert`, {}, { headers });
      if (data.status === 'sent') toast.success(`Test alert sent to ${data.channel}`);
      else toast.error(`Alert not sent: ${data.error || 'no channel configured'}`);
      load();
    } catch { toast.error('Send failed'); } finally { setBusy(false); }
  };

  if (denied) return <div className="max-w-2xl mx-auto p-10 text-center text-slate-600" data-testid="derwentai-denied">Admin access required to manage DERWENTAI Reports.</div>;
  if (!cfg) return <div className="p-10 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  const setReport = (k, field, val) => setCfg((c) => ({ ...c, reports: { ...c.reports, [k]: { ...c.reports[k], [field]: val } } }));
  const setTarget = (k, val) => setCfg((c) => ({ ...c, targets: { ...c.targets, [k]: val === '' ? null : Number(val) } }));
  const setAlert = (k, val) => setCfg((c) => ({ ...c, alerts: { ...c.alerts, [k]: Number(val) } }));

  const input = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2E333B]/20';
  const card = 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm';

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="derwentai-reports-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#2E333B] text-white"><Bot className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-[#2E333B] tracking-tight">DERWENTAI Reports</h1>
          <p className="text-sm text-slate-500">Automated Slack performance updates — your AI CEO / Operations Assistant, 3× per day (Melbourne time).</p>
        </div>
      </div>

      <ReportStatusCard status={status} card={card} onTestAlert={sendTestAlert} busy={busy} />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className={card}>
          <h2 className="font-bold text-[#2E333B] mb-4">Slack Delivery</h2>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Slack Channel or DM ID</label>
          <input className={input} value={cfg.channel || ''} onChange={(e) => setCfg((c) => ({ ...c, channel: e.target.value }))}
            placeholder="#daily-performance or C0123ABC" data-testid="derwentai-channel" />
          <p className="mt-1.5 text-xs text-slate-400">The Slack bot must be a member of this channel. Reports will not send until this is set.</p>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 mt-4">AI Summary Model</label>
          <select className={input} value={cfg.model} onChange={(e) => setCfg((c) => ({ ...c, model: e.target.value }))} data-testid="derwentai-model">
            <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (recommended)</option>
            <option value="gpt-5.4">GPT-5.4</option>
            <option value="gemini-3-flash-preview">Gemini 3 Flash</option>
          </select>
        </div>

        <div className={card}>
          <h2 className="font-bold text-[#2E333B] mb-4">Report Schedule (Melbourne, auto-DST)</h2>
          {REPORTS.map((r) => (
            <div key={r.key} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`derwentai-report-${r.key}`}>
              <input type="checkbox" checked={!!cfg.reports[r.key]?.enabled} onChange={(e) => setReport(r.key, 'enabled', e.target.checked)}
                className="h-5 w-5 rounded accent-[#2E333B]" data-testid={`derwentai-enabled-${r.key}`} />
              <span className="flex-1 text-sm font-semibold text-[#2E333B]">{r.label}</span>
              <span className="inline-flex items-center gap-1.5 text-slate-400"><Clock className="h-3.5 w-3.5" /></span>
              <input type="time" step="900" value={cfg.reports[r.key]?.time || ''} onChange={(e) => setReport(r.key, 'time', e.target.value)}
                className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-sm" data-testid={`derwentai-time-${r.key}`} />
              <button onClick={() => doPreview(r.key)} disabled={!!previewing} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-200" data-testid={`derwentai-preview-${r.key}`}>
                {previewing === r.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview
              </button>
              <button onClick={() => sendTest(r.key)} disabled={busy} className="inline-flex items-center gap-1 rounded-lg bg-[#2E333B] px-2.5 py-1.5 text-xs font-bold text-white hover:bg-[#3D434D]" data-testid={`derwentai-test-${r.key}`}>
                <Send className="h-3.5 w-3.5" /> Test
              </button>
            </div>
          ))}
          <p className="mt-2 text-xs text-slate-400">Times snap to 15-minute intervals (delivery cadence). Defaults: 5:30am · 1:00pm · 7:30pm.</p>
        </div>

        <div className={card}>
          <h2 className="font-bold text-[#2E333B] mb-4">Budget Targets ($ — blank uses Game Plan targets)</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Weekly Lodgement</label><input className={input} type="number" value={cfg.targets?.weekly_lodgement ?? ''} onChange={(e) => setTarget('weekly_lodgement', e.target.value)} data-testid="derwentai-wk-lodge" /></div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Monthly Lodgement</label><input className={input} type="number" value={cfg.targets?.monthly_lodgement ?? ''} onChange={(e) => setTarget('monthly_lodgement', e.target.value)} data-testid="derwentai-mo-lodge" /></div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Weekly Approval</label><input className={input} type="number" value={cfg.targets?.weekly_approval ?? ''} onChange={(e) => setTarget('weekly_approval', e.target.value)} /></div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Monthly Approval</label><input className={input} type="number" value={cfg.targets?.monthly_approval ?? ''} onChange={(e) => setTarget('monthly_approval', e.target.value)} /></div>
          </div>
        </div>

        <div className={card}>
          <h2 className="font-bold text-[#2E333B] mb-4">Alert Thresholds</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Large deal ($)</label><input className={input} type="number" value={cfg.alerts?.large_deal ?? ''} onChange={(e) => setAlert('large_deal', e.target.value)} /></div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Ready-to-lodge count</label><input className={input} type="number" value={cfg.alerts?.ready_to_lodge_count ?? ''} onChange={(e) => setAlert('ready_to_lodge_count', e.target.value)} /></div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">MIR backlog</label><input className={input} type="number" value={cfg.alerts?.mir_backlog ?? ''} onChange={(e) => setAlert('mir_backlog', e.target.value)} /></div>
            <div><label className="block text-xs font-bold text-slate-500 mb-1">Lead spike (daily)</label><input className={input} type="number" value={cfg.alerts?.lead_spike ?? ''} onChange={(e) => setAlert('lead_spike', e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-[#2E333B] px-6 py-3 font-bold text-white hover:bg-[#3D434D]" data-testid="derwentai-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
        </button>
      </div>

      {previewText && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="derwentai-preview-panel">
          <h3 className="font-bold text-[#2E333B] mb-2 flex items-center gap-2"><Eye className="h-4 w-4" /> Report Preview</h3>
          <pre className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700 font-sans">{previewText}</pre>
        </div>
      )}

      <div className={`mt-6 ${card}`}>
        <h2 className="font-bold text-[#2E333B] mb-4 flex items-center gap-2"><History className="h-4 w-4" /> Report History</h2>
        {history.length === 0 ? <p className="text-sm text-slate-500" data-testid="derwentai-history-empty">No reports generated yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="derwentai-history-table">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Time</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Status</th><th className="py-2">Summary</th>
              </tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-50">
                    <td className="py-2 pr-4 whitespace-nowrap">{h.local_date}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{h.local_time}</td>
                    <td className="py-2 pr-4 capitalize">{h.type}</td>
                    <td className="py-2 pr-4"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${h.status === 'sent' ? 'bg-emerald-50 text-emerald-700' : h.status === 'failed' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{h.status}</span>{(h.errors || []).length ? <span className="ml-2 text-xs text-rose-500">{h.errors.join(', ')}</span> : null}</td>
                    <td className="py-2 text-slate-600 max-w-md truncate">{h.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
